import { useMemo, useState, useCallback, useRef } from "react";
import { DEFAULT_FORM_VALUES } from "./constants";

/**
 * Klinik oluşturma/düzenleme formu state ve aksiyonları.
 *
 * Create akışı:
 *   Step 1 — Tüm bilgileri gir → "Oluştur" → doğrulama kodu gönderilir
 *   Step 2 — 6 haneli kodu gir → "Doğrula ve Oluştur" → klinik + yönetici oluşur
 *
 * Edit akışı:
 *   Tek adım — bilgileri güncelle → kaydet
 */
export function useClinicForm(
  token,
  sendClinicVerificationCode,
  verifyAndCreateClinic,
  updateClinic,
  uploadClinicLogo,
  onSuccess,
  showSnackbar
) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState("create"); // 'create' | 'edit'
  const [editingClinicId, setEditingClinicId] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_FORM_VALUES });

  // Doğrulama kodu state
  const [step, setStep] = useState(1); // 1: form, 2: kod girişi
  const [verificationCode, setVerificationCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeSentAt, setCodeSentAt] = useState(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef(null);

  const startResendCountdown = useCallback(() => {
    setResendCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setForm({ ...DEFAULT_FORM_VALUES });
    setEditingClinicId(null);
    setMode("create");
    setStep(1);
    setVerificationCode("");
    setCodeError("");
    setIsSendingCode(false);
    setIsVerifying(false);
    setCodeSentAt(null);
    setResendCountdown(0);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setDrawerOpen(true);
  }, [resetForm]);

  const openEdit = useCallback((clinic) => {
    if (!clinic?._id) return;
    setForm({
      name: clinic?.name ?? "",
      email: clinic?.email ?? "",
      clinicManagerName: "",
      clinicManagerSurname: "",
      clinicManagerGender: "",
      phone: clinic?.phone ?? "",
      website: clinic?.website ?? "",
      logoUrl: clinic?.logoUrl ?? "",
      logoIconUrl: clinic?.logoIconUrl ?? "",
      logoFile: null,
      logoIconFile: null,
      clearLogo: false,
      clearLogoIcon: false,
      workingSchedule: clinic?.workingSchedule ?? [],
      description: clinic?.description ?? "",
      status: clinic?.status ?? "active",
      isCorporate: Boolean(clinic?.isCorporate),
      taxNumber: clinic?.taxNumber ?? "",
      taxOffice: clinic?.taxOffice ?? "",
      city: clinic?.address?.city ?? "",
      district: clinic?.address?.district ?? "",
      fullAddress: clinic?.address?.fullAddress ?? "",
      themeColor: clinic?.themeColor ?? null,
    });
    setEditingClinicId(clinic._id);
    setMode("edit");
    setStep(1);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    resetForm();
  }, [resetForm]);

  const canSubmit = useMemo(() => {
    const {
      name, email,
      clinicManagerName, clinicManagerSurname, clinicManagerGender,
      clinicManagerTcNo, clinicManagerTellNo, clinicManagerPassword, clinicManagerPasswordConfirm,
      isCorporate, status, taxNumber, taxOffice,
    } = form;
    if (mode === "create") {
      const passwordMatch = clinicManagerPassword && clinicManagerPassword === clinicManagerPasswordConfirm;
      return Boolean(
        name?.trim() &&
        email?.trim() &&
        clinicManagerName?.trim() &&
        clinicManagerSurname?.trim() &&
        clinicManagerGender?.trim() &&
        clinicManagerTcNo?.trim()?.length === 11 &&
        clinicManagerTellNo?.trim() &&
        clinicManagerPassword?.length >= 8 &&
        passwordMatch
      );
    }
    const baseValid = Boolean(name?.trim() && email?.trim());
    const corporateActive = Boolean(isCorporate && status === "active");
    const taxValid = !corporateActive || (Boolean(taxNumber?.trim()) && Boolean(taxOffice?.trim()));
    return baseValid && taxValid;
  }, [mode, form]);

  // Step 1 → Step 2: Doğrulama kodu gönder
  const handleSendCode = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      setIsSendingCode(true);
      setCodeError("");
      try {
        await sendClinicVerificationCode({
          token,
          name: form.name,
          email: form.email,
          clinicManagerName: form.clinicManagerName,
          clinicManagerSurname: form.clinicManagerSurname,
          clinicManagerGender: form.clinicManagerGender,
          clinicManagerTcNo: form.clinicManagerTcNo,
          clinicManagerTellNo: form.clinicManagerTellNo,
          clinicManagerPassword: form.clinicManagerPassword,
        }).unwrap();
        setStep(2);
        setCodeSentAt(Date.now());
        startResendCountdown();
      } catch (err) {
        showSnackbar(err?.data?.message || "Doğrulama kodu gönderilemedi.", "error");
      } finally {
        setIsSendingCode(false);
      }
    },
    [token, form, sendClinicVerificationCode, showSnackbar, startResendCountdown]
  );

  // Tekrar kod gönder
  const handleResendCode = useCallback(async () => {
    if (resendCountdown > 0) return;
    await handleSendCode();
  }, [resendCountdown, handleSendCode]);

  // Step 2: Kodu doğrula ve klinik oluştur
  const handleVerifyAndCreate = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!verificationCode?.trim()) {
        setCodeError("Doğrulama kodunu giriniz.");
        return;
      }
      setIsVerifying(true);
      setCodeError("");
      try {
        await verifyAndCreateClinic({
          token,
          name: form.name,
          email: form.email,
          clinicManagerName: form.clinicManagerName,
          clinicManagerSurname: form.clinicManagerSurname,
          clinicManagerGender: form.clinicManagerGender,
          clinicManagerTcNo: form.clinicManagerTcNo,
          clinicManagerTellNo: form.clinicManagerTellNo,
          clinicManagerPassword: form.clinicManagerPassword,
          verificationCode: verificationCode.trim(),
          profiles: form.profiles?.length ? form.profiles : undefined,
        }).unwrap();
        showSnackbar("Klinik oluşturuldu.", "success");
        closeDrawer();
        onSuccess?.();
      } catch (err) {
        setCodeError(err?.data?.message || "Doğrulama başarısız.");
      } finally {
        setIsVerifying(false);
      }
    },
    [token, form, verificationCode, verifyAndCreateClinic, showSnackbar, closeDrawer, onSuccess]
  );

  // Geri dön (Step 2 → Step 1)
  const handleBackToForm = useCallback(() => {
    setStep(1);
    setVerificationCode("");
    setCodeError("");
  }, []);

  // Edit modu — güncelle
  const handleUpdate = useCallback(
    async (e) => {
      e.preventDefault();
      if (!editingClinicId) return;
      try {
        await updateClinic({
          token,
          clinicId: editingClinicId,
          name: form.name,
          email: form.email,
          phone: form.phone,
          website: form.website,
          logoUrl: form.clearLogo ? "" : form.logoUrl?.trim() || undefined,
          logoIconUrl: form.clearLogoIcon ? "" : form.logoIconUrl?.trim() || undefined,
          workingSchedule: form.workingSchedule,
          description: form.description,
          status: form.status,
          isCorporate: Boolean(form.isCorporate),
          taxNumber: form.taxNumber?.trim() || undefined,
          taxOffice: form.taxOffice?.trim() || undefined,
          themeColor: form.themeColor || null,
          address: {
            city: form.city,
            district: form.district,
            fullAddress: form.fullAddress,
            country: "Türkiye",
          },
        }).unwrap();
        if (form.logoFile && editingClinicId && uploadClinicLogo) {
          try {
            await uploadClinicLogo({ token, clinicId: editingClinicId, file: form.logoFile, logoType: "logo" }).unwrap();
          } catch (uploadErr) {
            showSnackbar(uploadErr?.data?.message || "Logo yüklenemedi.", "error");
          }
        }
        if (form.logoIconFile && editingClinicId && uploadClinicLogo) {
          try {
            await uploadClinicLogo({ token, clinicId: editingClinicId, file: form.logoIconFile, logoType: "icon" }).unwrap();
          } catch (uploadErr) {
            showSnackbar(uploadErr?.data?.message || "Logo ikonu yüklenemedi.", "error");
          }
        }
        showSnackbar("Klinik güncellendi.", "success");
        closeDrawer();
        onSuccess?.();
      } catch (err) {
        showSnackbar(err?.data?.message || "Klinik güncellenemedi.", "error");
      }
    },
    [token, editingClinicId, form, updateClinic, uploadClinicLogo, showSnackbar, closeDrawer, onSuccess]
  );

  const handleSubmit = mode === "edit" ? handleUpdate : handleSendCode;

  return {
    drawerOpen,
    closeDrawer,
    openCreate,
    openEdit,
    form,
    setField,
    handleSubmit,
    canSubmit,
    isCreate: mode === "create",
    isEdit: mode === "edit",
    // Doğrulama kodu
    step,
    verificationCode,
    setVerificationCode,
    codeError,
    isSendingCode,
    isVerifying,
    resendCountdown,
    handleVerifyAndCreate,
    handleResendCode,
    handleBackToForm,
  };
}

export default useClinicForm;
